import { Autocomplete, Box, FormControl, type SxProps, type Theme } from "@mui/material";
import type {
	AutocompleteInputChangeReason,
	AutocompleteProps,
	AutocompleteRenderGroupParams,
	AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import type { AutocompleteCloseReason } from "@mui/material/useAutocomplete";
import type { CSSProperties, JSX, ReactNode, SyntheticEvent } from "react";
import { INK, NEUTRAL } from "../../../../theme/tokens";
import { type SystemTextFieldProps, SystemValidationTextField } from "../SystemValidationTextField";
import { VirtualizedListBoxComponent } from "./VirtualizedListBox";

export type SystemAutoCompleteOption<TValue> = { label: string; value: TValue; disabled?: boolean };
type SystemAutoCompleteSlotProps<TValue> = AutocompleteProps<SystemAutoCompleteOption<TValue>, false, false, boolean>["slotProps"];
type Props<TValue> = {
	renderInput?: (params: AutocompleteRenderInputParams) => ReactNode;
	textFieldProps?: SystemTextFieldProps;
	options: SystemAutoCompleteOption<TValue>[];
	value?: SystemAutoCompleteOption<TValue> | null;
	onChange?: (event: SyntheticEvent, value: SystemAutoCompleteOption<TValue> | null) => void;
	disabled?: boolean;
	sx?: SxProps<Theme>;
	getOptionEndAdornment?: (option: SystemAutoCompleteOption<TValue>) => ReactNode;
	getOptionStartAdornment?: (option: SystemAutoCompleteOption<TValue>) => ReactNode;
	justifyContent?: CSSProperties["justifyContent"];
	onOpenStateChange?: (event: SyntheticEvent, reason: AutocompleteCloseReason | undefined, isOpen: boolean) => void;
	autoFocus?: boolean;
	fullWidth?: boolean;
	filterOptions?: (options: SystemAutoCompleteOption<TValue>[], state: { inputValue: string }) => SystemAutoCompleteOption<TValue>[];
	freeSolo?: boolean;
	/**
	 * Show the open/close chevron even when MUI would hide it. Optional and unset by default, so every
	 * existing caller keeps MUI's `"auto"` behaviour.
	 *
	 * Worth knowing: `"auto"` hides the chevron whenever `freeSolo` is on, which is right for a field
	 * that mainly accepts typed text (an id, a free label) and wrong for a `freeSolo` field that is
	 * really a PICKER with a typed escape hatch — there, the missing chevron leaves no affordance to
	 * browse, and the list can only be reached by typing into it. Pass `true` for that case.
	 */
	forcePopupIcon?: boolean | "auto";
	dataTestId?: string;
	groupBy?: (option: SystemAutoCompleteOption<TValue>) => string;
	renderGroup?: (params: AutocompleteRenderGroupParams) => ReactNode;
	slotProps?: SystemAutoCompleteSlotProps<TValue>;
	open?: boolean;
	disablePortal?: boolean;
	getOptionDataTestId?: (option: SystemAutoCompleteOption<TValue>) => string | undefined;
	/**
	 * Replace the option row's LABEL REGION with richer content — a primary line plus a muted secondary
	 * value (a name and the id it stands for), a match highlight, an inline meter. Optional and unset by
	 * default, so every existing caller keeps the plain single-line label.
	 *
	 * Scoped to the label on purpose rather than exposing MUI's whole `renderOption`: the `<li>` keeps
	 * owning the things a caller must not accidentally drop — the option test id, the start/end
	 * adornments, the selected/disabled styling, and the positional `style` the virtualizer merges in.
	 *
	 * Two constraints the caller owns: the returned node is a flex child of the row, so it should carry
	 * its own `minWidth: 0` + ellipsis to stay truncatable; and it must stay **single-line**, because the
	 * virtualized listbox lays rows out at a fixed height and a second line is clipped, not accommodated.
	 *
	 * `getOptionLabel` still drives FILTERING and the input's text, so a caller that moves the id out of
	 * the visible line must keep it in the option's `label` or typing that id stops matching.
	 */
	renderOptionLabel?: (option: SystemAutoCompleteOption<TValue>, state: { isSelected: boolean }) => ReactNode;
	/**
	 * Whether picking a value fades every OTHER option to 50% while the list is open (the default, and
	 * what every caller had before this prop existed).
	 *
	 * Pass `false` for a list that is BROWSED with a value already set — a roster picker whose whole job
	 * is re-pointing an existing mapping — where fading the alternatives dims exactly the rows the
	 * operator opened the list to read.
	 */
	dimUnselectedOptions?: boolean;
	/** Controlled text value of the input — pair with `onInputChange` for free-typing fields. */
	inputValue?: string;
	/** Fires on every keystroke (not just selection) — needed for live-parsed free-text inputs. */
	onInputChange?: (event: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => void;
	/** Options are being fetched — renders MUI's in-field progress indicator + "loading" listbox copy. */
	loading?: boolean;
	/**
	 * Annie walkthrough anchor (`context/standards/frontend.md`). Explicit because this component doesn't
	 * spread rest props, and TypeScript exempts hyphenated JSX attributes from excess-property checking —
	 * so an unknown `data-*` prop is dropped silently rather than failing the build.
	 */
	"data-annie-target"?: string;
};

export const SystemAutoComplete = <TValue,>({
	renderInput,
	textFieldProps,
	options,
	value,
	onChange,
	disabled,
	sx,
	getOptionEndAdornment,
	getOptionStartAdornment,
	justifyContent = "space-between",
	onOpenStateChange,
	autoFocus = false,
	fullWidth,
	filterOptions,
	freeSolo = false,
	forcePopupIcon,
	dataTestId,
	groupBy,
	renderGroup,
	slotProps,
	open,
	disablePortal,
	getOptionDataTestId,
	renderOptionLabel,
	// DEFAULT: `true` is not a preference, it is the behaviour every one of this component's callers
	// already had — the prop exists so a browse-while-set list can turn it off, and defaulting it any
	// other way would silently restyle all of them.
	dimUnselectedOptions = true,
	inputValue,
	onInputChange,
	loading,
	"data-annie-target": annieTarget,
}: Props<TValue>): JSX.Element => {
	// react-window can't lay out group headers, so we only swap in the virtualized listbox
	// when no `groupBy` is set. Plain (flat) lists get virtualization; grouped lists fall back
	// to MUI's default listbox.
	const useVirtualizedListbox = !groupBy;

	return (
		<FormControl fullWidth={fullWidth} data-testid={dataTestId} data-annie-target={annieTarget}>
			<Autocomplete
				loading={loading}
				filterOptions={filterOptions}
				autoFocus={autoFocus}
				onClose={onOpenStateChange ? (event, reason) => onOpenStateChange(event, reason, false) : undefined}
				onOpen={onOpenStateChange ? (event) => onOpenStateChange(event, undefined, true) : undefined}
				open={open}
				disablePortal={disablePortal}
				groupBy={groupBy}
				renderGroup={renderGroup}
				slots={useVirtualizedListbox ? { listbox: VirtualizedListBoxComponent } : undefined}
				slotProps={slotProps}
				disableListWrap
				disabled={disabled}
				options={options}
				getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label)}
				isOptionEqualToValue={(opt, val) => {
					const optValue = typeof opt === "string" ? opt : opt.value;
					const valValue = typeof val === "string" ? val : val.value;
					return optValue === valValue;
				}}
				getOptionDisabled={(option) => !!option.disabled}
				freeSolo={freeSolo}
				forcePopupIcon={forcePopupIcon}
				autoSelect={freeSolo}
				inputValue={inputValue}
				onInputChange={onInputChange ? (event, value, reason) => onInputChange(event, value, reason) : undefined}
				sx={sx}
				renderOption={(props, option, state) => {
					const { key, style, ...rest } = props;
					const isLastOption = state.index >= options.length - 1;
					const isSelected = option.value === value?.value;

					return (
						<li
							key={`${key}-${state.index}`}
							{...rest}
							data-testid={getOptionDataTestId?.(option)}
							style={{
								...style,
								color: INK.SECONDARY,
								opacity: dimUnselectedOptions && value && !isSelected ? 0.5 : option.disabled ? 0.4 : 1,
								borderBottom: isLastOption ? "none" : `1px solid ${NEUTRAL[300]}`,
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: justifyContent,
									width: "100%",
									fontSize: "14px",
								}}
							>
								{/* End adornment (keep it single-line to preserve virtualization height) */}
								{getOptionStartAdornment && (
									<Box
										sx={{
											mr: 1,
											flexShrink: 0,
											display: "inline-flex",
											alignItems: "center",
											gap: 0.5,
										}}
										// prevent selecting the option when clicking the adornment
										onMouseDown={(e) => e.preventDefault()}
										onClick={(e) => e.stopPropagation()}
									>
										{getOptionStartAdornment(option)}
									</Box>
								)}
								{renderOptionLabel ? (
									renderOptionLabel(option, { isSelected })
								) : (
									<Box
										sx={{
											overflow: "hidden",
											whiteSpace: "nowrap",
											textOverflow: "ellipsis",
											minWidth: 0,
											fontWeight: isSelected ? 600 : 400,
											color: INK.SECONDARY,
										}}
										title={option.label}
									>
										{option.label}
									</Box>
								)}

								{/* End adornment (keep it single-line to preserve virtualization height) */}
								{(() => {
									const endAdornmentNode = getOptionEndAdornment?.(option);
									return endAdornmentNode ? (
										<Box
											sx={{
												ml: 1,
												flexShrink: 0,
												display: "inline-flex",
												alignItems: "center",
												gap: 0.5,
											}}
											// prevent selecting the option when clicking the adornment
											onMouseDown={(e) => e.preventDefault()}
											onClick={(e) => e.stopPropagation()}
										>
											{endAdornmentNode}
										</Box>
									) : null;
								})()}
							</Box>
						</li>
					);
				}}
				onChange={(event, newValue) => {
					if (typeof newValue === "string") {
						onChange?.(event, newValue ? { label: newValue, value: newValue as unknown as TValue } : null);
					} else {
						onChange?.(event, newValue);
					}
				}}
				value={value}
				renderInput={
					renderInput
						? renderInput
						: (params) => <SystemValidationTextField isInsideAutoComplete={true} injectedProps={params} fullWidth={fullWidth} {...textFieldProps} />
				}
			/>
		</FormControl>
	);
};
